import { ExternalLink, Presentation } from 'lucide-react';
import { formatPlainDate } from '@cfl/shared';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/cargofl-ui';
import { useShowcaseLog } from '@/lib/dev-connect-api';

/**
 * What this person showed at Dev Connect during the quarter being reviewed.
 *
 * Fetches its own rows rather than riding the assessment payload, the same way
 * EvidencePanel does on this screen: buildView on the API is deliberately pure and
 * shared with the reviews queue, so threading this through it would put a join on
 * every row of a list that never renders it.
 *
 * Renders nothing at all when there is none. An empty card on every assessment,
 * for the great majority who have not presented, is noise on the most-read screen
 * in the app - and an empty state here would read as an absence being marked
 * against somebody, which is exactly what this is not.
 */
export function DevConnectCard({
  employeeId,
  quarterId,
  quarterLabel,
}: {
  employeeId: string;
  quarterId: string;
  quarterLabel: string;
}) {
  const { data } = useShowcaseLog({ employeeId, quarterId });
  const entries = data ?? [];
  if (entries.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Presentation className="size-4 text-muted-foreground" />
          Dev Connect
          <span className="pms-num text-sm font-normal text-muted-foreground">
            · {entries.length} {entries.length === 1 ? 'talk' : 'talks'}
          </span>
        </CardTitle>
        <CardDescription>
          Showcased during {quarterLabel}. Context for the review — it carries no rating.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {entries.map((entry) => (
          <div key={entry.id} className="rounded-lg border bg-muted/40 p-3">
            <p className="text-sm font-medium">{entry.title}</p>
            <p className="text-xs text-muted-foreground">{formatPlainDate(entry.heldOn)}</p>
            {entry.notes && <p className="mt-1 text-sm text-muted-foreground">{entry.notes}</p>}
            <div className="mt-1 flex flex-wrap gap-3">
              {[
                { href: entry.demoUrl, label: 'Demo' },
                { href: entry.slidesUrl, label: 'Slides' },
                { href: entry.prUrl, label: 'PR' },
              ]
                .filter((l): l is { href: string; label: string } => Boolean(l.href))
                .map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="size-3" />
                    {l.label}
                  </a>
                ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
